import { isPlainObject } from 'lodash';
import workOrderTypes from '@/utilities/enums/workOrderTypes';
import type {
    SummaryReport,
    AnalysisRequest,
    WorkOrderLabInfo,
    WorkOrderWithActivities,
    AnalysisViewItem,
    AnalysisView,
    ActionContext,
    Measure,
} from '@/interfaces/common';
import type { NodeTree } from '@/interfaces/site-tree.types';
import $rq from '@/api/api';

interface WorkOrderState {
    workOrder: Partial<WorkOrderWithActivities>;
    analysisRequestsList: AnalysisRequest[];
    workOrderType: string;
    workOrderLabInfo: WorkOrderLabInfo;
    primedWorkOrderId: string | null;
}

// Default state factory
const defaultState = (): WorkOrderState => ({
    workOrder: {},
    analysisRequestsList: [],
    workOrderType: workOrderTypes.VISIT,
    workOrderLabInfo: {
        site: '',
        client: '',
        preSelectedSamples: [],
    },
    primedWorkOrderId: null,
});

// Mutations
const mutations = {
    setWorkOrderState(state: WorkOrderState, workOrder: Partial<WorkOrderWithActivities>) {
        state.workOrder = workOrder;
        state.primedWorkOrderId = (workOrder?.id as string | undefined) || null;
    },
    consumePrimedWorkOrderId(state: WorkOrderState) {
        state.primedWorkOrderId = null;
    },
    updateAnalysisWithHistory(
        state: WorkOrderState,
        { analysisId, history }: { analysisId: string; history: unknown },
    ) {
        if (!state.workOrder.activities?.measuresAndSamplesCollect?.tasks?.analyses) return;

        const analyses = state.workOrder.activities.measuresAndSamplesCollect.tasks.analyses;
        const index = analyses.findIndex((a) => (a.id || a._id) === analysisId);

        if (index !== -1) {
            // Create a new array with the updated analysis
            const updatedAnalyses = [...analyses];
            // Use type assertion to tell TypeScript that history has the correct structure
            updatedAnalyses[index] = {
                ...updatedAnalyses[index],
                history: history as { measures: Measure[] },
            };

            // Update the state with the new array
            state.workOrder.activities.measuresAndSamplesCollect.tasks.analyses = updatedAnalyses;
        }
    },
    updateAnalysisMeasureValue(
        state: WorkOrderState,
        { analysisId, measure }: { analysisId: string; measure: Record<string, unknown> },
    ) {
        if (!state.workOrder.activities?.measuresAndSamplesCollect?.tasks?.analyses) return;

        const analyses = state.workOrder.activities.measuresAndSamplesCollect.tasks.analyses;
        const index = analyses.findIndex((a) => (a.id || a._id) === analysisId);

        if (index !== -1) {
            // Create a new array with the updated analysis
            const updatedAnalyses = [...analyses];
            const currentMeasure = updatedAnalyses[index].measure || {};
            updatedAnalyses[index] = {
                ...updatedAnalyses[index],
                measure: { ...currentMeasure, ...measure },
            };

            // Update the state with the new array
            state.workOrder.activities.measuresAndSamplesCollect.tasks.analyses = updatedAnalyses;
        }
    },
    resetWorkOrderState(state: WorkOrderState) {
        Object.assign(state, defaultState());
    },
    setWorkOrderAnalysisRequestsState(state: WorkOrderState, analysisRequests: AnalysisRequest[]) {
        state.analysisRequestsList = analysisRequests;
    },
    resetWorkOrderAnalysisRequestsState(state: WorkOrderState) {
        state.analysisRequestsList = [];
    },
    setWorkOrderType(state: WorkOrderState, type: string) {
        state.workOrderType = type;
    },
    setWorkOrderLabInfo(state: WorkOrderState, workOrderLabInfo: WorkOrderLabInfo) {
        state.workOrderLabInfo = workOrderLabInfo;
    },
    updateSummaryReport(state: WorkOrderState, payload: Partial<SummaryReport>) {
        const { workOrder } = state;
        const summaryReport = isPlainObject(workOrder.summaryReport) ? workOrder.summaryReport : {};

        state.workOrder.summaryReport = {
            ...summaryReport,
            ...payload,
        } as SummaryReport;
    },
};

// Actions
const actions = {
    updateAnalysisHistory(
        { commit }: ActionContext,
        payload: { analysisId: string; history: unknown },
    ) {
        commit('updateAnalysisWithHistory', payload);
    },
    updateAnalysisMeasure(
        { commit }: ActionContext,
        payload: { analysisId: string; measure: Record<string, unknown> },
    ) {
        commit('updateAnalysisMeasureValue', payload);
    },
    async getWorkOrderInfo({ commit, dispatch, state }: ActionContext, workOrderId: string) {
        const primedState = state as WorkOrderState;
        if (primedState.primedWorkOrderId === workOrderId) {
            commit('consumePrimedWorkOrderId');
            return;
        }
        try {
            const { data } = await $rq.getWorkOrder(workOrderId);
            commit('setWorkOrderState', data);
            commit('consumePrimedWorkOrderId');
            dispatch('getWorkOrderAnalysisRequests', workOrderId);
        } catch (err) {
            dispatch('resetWorkOrderInfo');
        }
    },
    async getWorkOrderAnalysisRequests({ commit, dispatch }: ActionContext, workOrderId: string) {
        try {
            const queryParams = {
                workOrder: workOrderId,
                perpage: 100,
            };
            const { data } = await $rq.getAnalysisRequests(queryParams);
            commit('setWorkOrderAnalysisRequestsState', data.analysisRequests);
        } catch (err) {
            dispatch('resetWorkOrderAnalysisRequests');
        }
    },
    setWorkOrderType({ commit }: ActionContext, type: string) {
        commit('setWorkOrderType', type);
    },
    setWorkOrderLabInfo({ commit }: ActionContext, workOrderLabInfo: WorkOrderLabInfo) {
        commit('setWorkOrderLabInfo', workOrderLabInfo);
    },
    resetWorkOrderInfo({ commit }: ActionContext) {
        commit('resetWorkOrderState');
    },
    resetWorkOrderAnalysisRequests({ commit }: ActionContext) {
        commit('resetWorkOrderAnalysisRequestsState');
    },
};
// Getters
const getters = {
    workOrder: (state: WorkOrderState) => state.workOrder,
    analysisRequestsList: (state: WorkOrderState) => state.analysisRequestsList,
    workOrderType: (state: WorkOrderState) => state.workOrderType,
    workOrderLabInfo: (state: WorkOrderState) => state.workOrderLabInfo,
    analysisView: (state: WorkOrderState): AnalysisView => {
        if (!state.workOrder.activities?.measuresAndSamplesCollect) {
            return {};
        }

        const { tree, analysisTypes, tasks } = state.workOrder.activities.measuresAndSamplesCollect;
        const analysisView: AnalysisView = {};

        const componentByRef = new Map<string, NodeTree>();
        const visit = (nodes?: NodeTree[]) => {
            if (!nodes) return;
            for (const node of nodes) {
                if (node.ref) componentByRef.set(node.ref, node);
                if (node.children) visit(node.children);
            }
        };
        visit(tree);

        const analysisTypeByRef = new Map<string, (typeof analysisTypes)[number]>();
        for (const at of analysisTypes) {
            if (at.ref) analysisTypeByRef.set(at.ref, at);
        }

        tasks.analyses.forEach((analysis) => {
            const id = analysis.analysisType.ref!;
            const analysisType = analysisTypeByRef.get(id);
            const component = componentByRef.get(analysis.source!.ref!);

            if (!analysisView[id]) {
                analysisView[id] = {} as AnalysisViewItem;
                analysisView[id].analysisType = analysisType!;
            }

            if (!analysisView[id].analyses) {
                analysisView[id].analyses = [];
            }

            analysisView[id].analyses.push({
                ...analysis,
                analysisType: analysisType!,
                component,
            });
        });

        return analysisView;
    },
    summaryReport: (state: WorkOrderState): SummaryReport => {
        const { summaryReport } = state.workOrder;
        return isPlainObject(summaryReport)
            ? (summaryReport as SummaryReport)
            : ({} as SummaryReport);
    },
};

// Initial state
const state = defaultState();

export default {
    state,
    getters,
    actions,
    mutations,
};
